import { FC, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useAccess } from '../../hooks/useAccess';
import { useHandleField } from '../../hooks/useHandleField';
import { useTypedSelector } from '../../hooks/useTypedSelector';
import { useUser } from '../../hooks/useUser';
import { IEditUser, IEditUserPassword, IUser } from '../../model/IUser';
import { msg } from '../../msg';
import { getCurrentUser } from '../../store/authSlice';
import { getVendors } from '../../store/commonSlice';
import { IZodErrorsState } from '../../utils/checkFormStateByZod';
import Button from '../form/Button';
import FormBlock from '../form/FormBlock';
import Input from '../form/Input';
import Switch from '../form/Switch';
import UserEditRole from './UserEditRole';
import UserRoleInfo from './UserRoleInfo';
import { useAuth } from '../../hooks/useAuth';

const UserEditForm: FC<{
  user: IUser,
  showAssignable?: boolean,
  // --
  // -- Off by default, and deliberately not shown on the Account page: that
  // -- page's user comes from /me, which omits the token, so the field would
  // -- render blank and an admin editing their own account would clear their
  // -- own credential by saving.
  // --
  showIntegrations?: boolean,
  // withoutCurrentPassword?: boolean,
}> = ({
  user,
  showAssignable = true,
  showIntegrations = false,
  // withoutCurrentPassword = false,
}) => {
    const currentUser = useTypedSelector(getCurrentUser());
    const isOwner = currentUser?.id === user.id;
    const { setMyself } = useAuth();

    const { editMainUserInfo, editPassUserInfo } = useUser();
    const navigate = useNavigate();
    const vendorList = useTypedSelector(getVendors());
    const { convertRolesToIdsStr, convertVendorFullRolesToRolesStr, convertAgentFullRolesToRolesStr } = useUser();

    // -- Check permissions
    const { getSettingAccess, getPageAccess } = useAccess();
    // -- The token is a credential for sending as someone, so it is an admin
    // -- field even on a page an owner can otherwise open.
    const canEditIntegrations = showIntegrations && getSettingAccess('edit-user');
    useEffect(() => {
      if (!getSettingAccess('edit-user')) {
        if (!isOwner) navigate('/users');
        else if (!getPageAccess('/account')) navigate('/users');
      }
    }, []);

    // -- Init form state
    const initState: IEditUser = {
      name: user?.name || '',
      phone: user?.phone || '',
      isAllowedForTickets: user?.isAllowedForTickets ?? true,
      isSearchableSales: user?.isSearchableSales ?? true,
      isSearchableTickets: user?.isSearchableTickets ?? true,
      isActive: user?.active ?? true,
      roles: user?.userRole?.length && convertRolesToIdsStr(user.userRole) || 'ROLE_USER',
      vendor: String(user?.vendorRole?.[0]?.vendor?.id) || '',
      vendorRole: convertVendorFullRolesToRolesStr(user?.vendorRole),
      agentRole: convertAgentFullRolesToRolesStr(user?.agentRole),
      // -- Always tracked, never always sent. Seeding it conditionally looked
      // -- tidier but useState only reads initState once: if the admin check
      // -- resolves after mount, the field renders with no state behind it and
      // -- shows blank over a token that is really set. Whether to send it is
      // -- decided in handleEditUser instead, where the answer is current.
      mightycallUserToken: user?.mightycallUserToken || '',
    };

    const initPasswordState: IEditUserPassword = {
      password: '',
      passwordConfirm: '',
      currentPassword: '',
    }

    // -- States
    const [userState, setUserState] = useState<IEditUser>(initState);
    // -- IEditUser, not IUserRequest: this is what editMainUserInfo returns,
    // -- and the two shapes differ.
    const [userErrState, setUserErrState] = useState<Partial<IZodErrorsState<IEditUser>>>();
    const [userStatePassword, setUserStatePassword] = useState<IEditUserPassword>(initPasswordState);
    const [userErrStatePassword, setUserErrStatePassword] = useState<Partial<IZodErrorsState<IEditUserPassword>>>();
    const [isPending, setIsPending] = useState<boolean>(() => false);

    // -- Fill default vendor
    useEffect(() => {
      vendorList?.length && !userState?.vendor && setUserState((prevState) => ({
        ...prevState,
        vendor: String(vendorList?.[0]?.id) || '',
      }));
    }, [vendorList, userState, user]);

    const handle = useHandleField(setUserState);
    const handlePswrd = useHandleField(setUserStatePassword);
    const handleCheckbox = (key: Partial<keyof typeof initState>) => () =>
      setUserState((prevState) => ({
        ...prevState,
        [key]: !!!prevState?.[key],
      }));

    const handleEditUser = async () => {
      setIsPending(() => true);
      setUserErrState(() => undefined);

      // -- Present-but-empty tells the server to clear the token, so a form
      // -- that never showed the field must omit the key entirely - otherwise
      // -- the Account page, whose /me user carries no token, would blank it.
      const { mightycallUserToken, ...withoutToken } = userState;
      const payload = canEditIntegrations ? userState : withoutToken;

      const errs = await editMainUserInfo(user.id, payload);
      if (errs) {
        setUserErrState(() => errs);
        setIsPending(() => false);
        return;
      }

      currentUser?.id === user.id && (await setMyself());
      toast.success(msg('user-edited'));
      setIsPending(() => false);
      // return navigate('/users'); 
    }

    const handleEditUserPass = async () => {
      setIsPending(() => true);
      setUserErrStatePassword(() => undefined);

      const errs = await editPassUserInfo(user.id, userStatePassword);
      if (errs) {
        setUserErrStatePassword(() => errs);
        setIsPending(() => false);
        return;
      }

      toast.success(msg('user-edited'));
      setIsPending(() => false);
      // return navigate('/users'); 
    }

    const handleResetUser = () => setUserState(() => initState);

    return (
      <>
        {/* <ResetApplyBtns
        isPending={isPending}
        handleResetUser={handleResetUser}
        handleEditUser={handleEditUser}
      /> */}

        {showAssignable && (
          <FormBlock
            title="User Accessibility"
            subTitle="Modify user options"
            className="bg-gray-50 border-t-0"
          >
            <Switch
              label="Allow automatic ticket assignment"
              name="isAllowedForTickets"
              value={userState.isAllowedForTickets ?? false}
              handleToggle={handleCheckbox('isAllowedForTickets')}
              className='col-span-6 xl:col-span-3 justify-center'
              disabled={isPending}
              isNoLabelArea
            />
            <Switch
              label="Searchable On Sales Portal"
              name="isSearchableSales"
              value={userState.isSearchableSales ?? true}
              handleToggle={handleCheckbox('isSearchableSales')}
              className='col-span-6 xl:col-span-3 justify-center'
              disabled={isPending}
              isNoLabelArea
            />
            <Switch
              label="Searchable On CS Portal"
              name="isSearchableTickets"
              value={userState.isSearchableTickets ?? true}
              handleToggle={handleCheckbox('isSearchableTickets')}
              className='col-span-6 xl:col-span-3 justify-center'
              disabled={isPending}
              isNoLabelArea
            />

            {/* <Input
            label="Period (in hours)"
            name="email"              
            value={''}              
            className='col-span-6 xl:col-span-3'
            description="After this time the activity will be toggled."
          /> 
          <Input
            label="Comments"
            name="email"              
            value={''}              
            className='col-span-12 xl:col-span-6'
            description="example: The user took 2 days off"
          />  */}
          </FormBlock>
        )}

        <FormBlock
          title="Basic"
          subTitle="Basic User Information"
        >
          <Input
            label="Email"
            name="email"
            value={user?.email || ''}
            className='col-span-6 xl:col-span-3'
            disabled
          />
          <Input
            label="Name"
            name="name"
            placeholder='Manager name'
            value={userState.name}
            handle={handle('name')}
            className='col-span-6 xl:col-span-3'
            error={userErrState?.name}
            disabled={isPending || !getSettingAccess('edit-user')}
          />
          <Input
            label="Phone"
            name="phone"
            placeholder='Manager phone'
            value={userState.phone}
            handle={handle('phone')}
            className='col-span-6 xl:col-span-3'
            error={userErrState?.phone}
            disabled={isPending || !getSettingAccess('edit-user')}
          />
          {!!getSettingAccess('edit-enable') && (
            <Switch
              label="Enable"
              size="small"
              name="isActive"
              value={userState.isActive ?? false}
              handleToggle={handleCheckbox('isActive')}
              className='col-span-6 xl:col-span-3 justify-center'
              disabled={isPending}
            />
          )}
        </FormBlock>

        {canEditIntegrations && (
          <FormBlock
            title="Integrations"
          >
            <Input
              label="MightyCall User Token"
              name="mightycallUserToken"
              placeholder="Leave empty to send as Team Automotix"
              value={userState.mightycallUserToken ?? ''}
              handle={handle('mightycallUserToken')}
              className='col-span-12 xl:col-span-6'
              error={userErrState?.mightycallUserToken}
              disabled={isPending}
            />
          </FormBlock>
        )}

        <FormBlock
          title="Roles"
          subTitle="All Roles Available to the User"
          className="bg-gray-50 border-b-[1px]"
        >
          {getSettingAccess('edit-user')
            ? <UserEditRole
              userState={userState}
              handle={handle}
              userErrState={userErrState}
              isPending={isPending}
              className="col-span-12"
            />
            : <UserRoleInfo className="col-span-12"
              {...user}
            />
          }
        </FormBlock>

        <ResetApplyBtns
          isPending={isPending}
          handleResetUser={handleResetUser}
          handleEditUser={handleEditUser}
          className=""
        />

        <FormBlock
          title="Passwords"
          subTitle="Change the Password"
          className="bg-gray-50 border-b-[1px]"
        >
          <Input
            label='Password (required)'
            name="password"
            value={userStatePassword.password}
            handle={handlePswrd('password')}
            type="password"
            className='col-span-6'
            error={userErrStatePassword?.password}
            disabled={isPending}
          />
          <Input
            label='Confirm Password (required)'
            name="passwordConformed"
            value={userStatePassword.passwordConfirm}
            handle={handlePswrd('passwordConfirm')}
            type="password"
            className='col-span-6'
            error={userErrStatePassword?.passwordConfirm}
            disabled={isPending}
          />
          {currentUser?.id === user?.id && (
            <Input
              label='Your Password'
              name="currentPassword"
              value={userStatePassword.currentPassword}
              handle={handlePswrd('currentPassword')}
              type="password"
              className='col-span-12'
              error={userErrStatePassword?.currentPassword}
              disabled={isPending}
            />
          )}

          <div className="col-span-12 flex justify-end">
            <Button
              type="submit"
              onClick={handleEditUserPass}
              isNotFullWidth
              disabled={isPending}
              className="col-span-4"
            >
              Edit Password
            </Button>
          </div>
        </FormBlock>
      </>
    );
  }

const ResetApplyBtns: FC<{
  isPending: boolean,
  handleResetUser: () => unknown,
  handleEditUser: () => unknown,
  className?: string,
}> = ({
  isPending,
  handleResetUser,
  handleEditUser,
  className,
}) => (
    <div className={`flex justify-end gap-4 my-6 col-span-12 p-4 ${className}`}>
      <Button
        onClick={handleResetUser}
        isNotFullWidth
        disabled={isPending}
      >
        Reset
      </Button>
      <Button
        onClick={handleEditUser}
        isNotFullWidth
        disabled={isPending}
        type="submit"
      >
        Edit
      </Button>
    </div>
  );

export default UserEditForm;
